import { useEffect, useRef } from 'react';
import Cloze from '../review/Cloze';

/**
 * Stage 4 — Recall (cloze in the onboarding flow).
 *
 * Thin wrapper around the review `Cloze` component. The backend submit
 * response uses string ratings ("good" | "hard" | "again") rather than
 * the integer ratings the review answer endpoint returns, so we translate
 * before handing off to Cloze's expected shape:
 *   { correct: bool, correct_answer: string, rating: 1 | 2 | 3 }.
 *
 * Hint-and-retry (spec §4.8): backend may return `{ retry: true, hint, definition }`
 * on attempt-1-wrong; we forward that shape unchanged so Cloze's existing
 * retry handling kicks in (expanded prefix + definition reveal). Onboarding
 * thus matches the review flow exactly.
 *
 * If the backend payload carries `skip: true` (cloze couldn't be built —
 * usually a stale context_sentence that doesn't contain the target word),
 * we render a brief placeholder and auto-submit so the parent advances.
 */
const RATING_INT = { again: 1, hard: 2, good: 3 };

export default function Stage4Cloze({
  lemma,
  sentenceWithBlank,
  firstLetter,
  remainingLength,
  maskPattern,
  sentenceHtml,
  acceptedSurfaceForm,
  definition,
  pos,
  skip,
  debugReason,
  debugContext,
  onSubmit,
  // Controlled props passed through to Cloze for live-session mirror.
  text,
  result,
  onTextChange,
  onResult,
  hintOpen,
  onHintOpenChange,
}) {
  // The skip auto-submit has a brittle history: it must fire exactly once
  // per skip card so the parent can advance, but it has to survive
  //   (a) Strict Mode's mount → cleanup → mount double-invoke (which
  //       previously cleared the pending timer and then short-circuited
  //       via a boolean ref, trapping the student with no Continue)
  //   (b) back-to-back skip cards in the same component instance
  //   (c) parent re-renders that change `onSubmit`'s closure identity
  //
  // Strategy:
  //   - Don't clear the timer in useEffect cleanup — Strict Mode's
  //     mount/cleanup/mount kills it. Instead, gate the submit at fire
  //     time on `isMountedRef`.
  //   - Key the "already fired" guard by lemma, not a bare boolean.
  //   - Read the latest `onSubmit` via a ref so its identity changes
  //     don't enter the effect deps.
  // Protected by test/system/student/vocabulary_onboarding_skip_test.rb.
  const skipFiredForLemmaRef = useRef(null);
  const isMountedRef = useRef(true);
  const onSubmitRef = useRef(onSubmit);
  useEffect(() => {
    onSubmitRef.current = onSubmit;
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!skip) return;
    if (skipFiredForLemmaRef.current === lemma) return;
    skipFiredForLemmaRef.current = lemma;
    // Submit empty text — backend treats this as auto-pass when cloze
    // isn't buildable (handle_stage_4 short-circuits to :good). 1500ms
    // gives the student a beat to read the message; below ~800ms it
    // just flashes. No cleanup — see strategy note above.
    setTimeout(() => {
      if (isMountedRef.current) onSubmitRef.current('');
    }, 1500);
  }, [skip, lemma]);

  if (skip) {
    // Auto-submit fires after 1500ms; the parent OnboardingActivity then
    // shows its own Continue button. We deliberately do NOT render an
    // inline Continue here — the parent's button is the single advance
    // affordance.
    return (
      <div className="text-center py-12 space-y-4 max-w-xl mx-auto">
        <div className="text-sm text-gray-400 dark:text-gray-500 italic">
          Skipping <span className="font-serif">{lemma}</span> — couldn't build a cloze for this one.
        </div>
        {debugReason && (
          <details className="text-left text-xs text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-900/40 rounded-md px-3 py-2 border border-gray-100 dark:border-gray-800">
            <summary className="cursor-pointer font-mono">why? (dev only)</summary>
            <div className="mt-2 space-y-1 font-mono">
              <div><span className="text-gray-500 dark:text-gray-400">reason:</span> {debugReason}</div>
              {debugContext && (
                <div className="break-words">
                  <span className="text-gray-500 dark:text-gray-400">saved context_sentence:</span>{' '}
                  <span className="italic">"{debugContext}"</span>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    );
  }

  const handleAnswer = async ({ text, attempt, hint_used }) => {
    const data = await onSubmit(text, attempt, { hint_used });
    if (data?.retry) {
      // Pass the retry shape straight through; Cloze knows how to render it.
      // Carries `kind` so Cloze can distinguish a typo retry (show the
      // correct spelling) from a miss retry (show the letter hint).
      return data;
    }
    const ratingInt = RATING_INT[data?.rating] ?? 1;
    return {
      correct: ratingInt >= 3,
      correct_answer: data?.correct_answer,
      rating: ratingInt,
      kind: data?.kind,
    };
  };

  return (
    <div className="space-y-4">
      <p className="text-lg text-gray-700 dark:text-gray-300 text-center">
        Type the whole word.
      </p>
      <Cloze
        key={sentenceWithBlank}
        sentenceWithBlank={sentenceWithBlank}
        firstLetter={firstLetter}
        remainingLength={remainingLength}
        maskPattern={maskPattern}
        acceptedSurfaceForm={acceptedSurfaceForm}
        definition={definition}
        pos={pos}
        sentenceHtml={sentenceHtml}
        onAnswer={handleAnswer}
        // The heading above already says "Type the missing word." — drop
        // the placeholder so the input doesn't echo it (B3).
        placeholder=""
        text={text}
        result={result}
        onTextChange={onTextChange}
        onResult={onResult}
        hintOpen={hintOpen}
        onHintOpenChange={onHintOpenChange}
      />
    </div>
  );
}
